import { Controller } from "@hotwired/stimulus"

// Pay Applications list page: the New Pay Application modal + row navigation.
export default class extends Controller {
  static targets = ["newModal", "periodStart", "periodEnd", "retainage", "createBtn", "retainageNote",
                    "arModal", "arSend", "paymentModal", "paymentApp", "paymentAmount",
                    "paymentDate", "paymentMethod", "paymentReference", "paymentSave"]
  static values = { createUrl: String, retainage: Number, suggestUrl: String }

  connect() {
    const msg = sessionStorage.getItem("builder-finance.flash")
    if (msg) {
      sessionStorage.removeItem("builder-finance.flash")
      this._showToast(msg)
    }
    // What was typed in the New Pay Application pop-up comes back (K8-15,
    // Larry 2026-10-08): remembered per project until the application is created.
    this._restoreNewEntries()
  }

  openApp(e) {
    if (e.target.closest("button, a, input")) return
    const url = e.currentTarget.dataset.url
    if (url) window.location.href = url
  }

  openNewModal() {
    this.newModalTarget.classList.remove("hidden")
    this._suggestRetainage()
  }
  closeNewModal() { this.newModalTarget.classList.add("hidden") }
  stopPropagation(e) { e.stopPropagation() }

  // ── Keep what was typed (K8-15, Larry 2026-10-08) ──
  // Larry filled in the period end and retainage, clicked outside, and the
  // pop-up closed and threw it away — twice. Now: once anything in it has
  // been changed, a click outside leaves it open (Cancel and × still close
  // it), and every entry is remembered for this project until the
  // application is created, so even a closed pop-up or a reload brings
  // them back.
  closeNewModalFromBackdrop() {
    if (this._newEntriesTyped()) {
      if (!this._keptHintShown) { this._keptHintShown = true; this._showToast("Your entries are kept — use Cancel or × to close this.") }
      return
    }
    this.closeNewModal()
  }

  newEntriesChanged() {
    if (this._newEntriesTyped()) this._rememberNewEntries()
    else this._forgetNewEntries()
  }

  // "Typed" = different from what the pop-up opened with, so opening and
  // closing a date picker alone doesn't make it sticky.
  _newEntries() {
    return { period_start: this.periodStartTarget.value, period_end: this.periodEndTarget.value, retainage: this.retainageTarget.value }
  }

  _newEntriesTyped() {
    if (!this.hasPeriodStartTarget || !this.hasPeriodEndTarget || !this.hasRetainageTarget) return false
    const now = this._newEntries(), was = this._newDefaults || {}
    return Object.keys(now).some(k => String(now[k] ?? "") !== String(was[k] ?? ""))
  }

  _newEntriesKey() { return `gc-pay-app.new.${this.createUrlValue || window.location.pathname}` }

  _rememberNewEntries() {
    try { sessionStorage.setItem(this._newEntriesKey(), JSON.stringify(this._newEntries())) } catch (e) { /* private mode: nothing to remember into */ }
  }

  _restoreNewEntries() {
    if (!this.hasPeriodStartTarget || !this.hasPeriodEndTarget || !this.hasRetainageTarget) return
    this._newDefaults = this._newEntries()
    let saved = null
    try { saved = JSON.parse(sessionStorage.getItem(this._newEntriesKey()) || "null") } catch (e) { saved = null }
    if (!saved) return
    this._setDate(this.periodStartTarget, saved.period_start)
    this._setDate(this.periodEndTarget, saved.period_end)
    if (saved.retainage != null && saved.retainage !== "") this.retainageTarget.value = saved.retainage
  }

  _forgetNewEntries() {
    try { sessionStorage.removeItem(this._newEntriesKey()) } catch (e) { /* ignore */ }
  }

  // The date inputs are flatpickr pickers: set the date through them so the
  // visible (m/d/Y) box follows the stored (Y-m-d) value.
  _setDate(input, value) {
    if (value == null) return
    const fp = input._flatpickr
    if (fp) { value ? fp.setDate(value, false) : fp.clear(false) } else { input.value = value }
  }

  // ── Record a payment, from the list (Larry 2026-09-15) ──
  openPaymentModal() { if (this.hasPaymentModalTarget) this.paymentModalTarget.classList.remove("hidden") }
  closePaymentModal() { if (this.hasPaymentModalTarget) this.paymentModalTarget.classList.add("hidden") }

  async recordPayment() {
    const url = this.hasPaymentAppTarget ? this.paymentAppTarget.selectedOptions[0]?.dataset.url : null
    if (!url) return this._showToast("Pick which application this payment is for.", "alert")
    if (this.hasPaymentSaveTarget) this.paymentSaveTarget.disabled = true
    try {
      const resp = await fetch(url, { method: "POST", credentials: "same-origin", headers: this._jsonHeaders(),
        body: JSON.stringify({
          amount: this.paymentAmountTarget.value,
          paid_on: this.paymentDateTarget.value,
          method: this.paymentMethodTarget.value,
          reference: this.paymentReferenceTarget.value
        }) })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Could not record the payment")
      sessionStorage.setItem("builder-finance.flash", data.fully_paid ? "Payment recorded — paid in full 🎉" : "Payment recorded")
      window.location.reload()
    } catch (err) {
      this._showToast(err.message, "alert")
    } finally {
      if (this.hasPaymentSaveTarget) this.paymentSaveTarget.disabled = false
    }
  }

  async deletePayment(e) {
    const btn = e.currentTarget
    const row = btn.closest("tr")
    const resp = await fetch(btn.dataset.url, { method: "DELETE", credentials: "same-origin", headers: this._jsonHeaders() })
    if (!resp.ok) return this._showToast("Could not remove that payment", "alert")
    row?.remove()
    this._showToast("Payment removed")
  }

  _jsonHeaders() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    return { "Content-Type": "application/json", "X-CSRF-Token": token || "", "Accept": "application/json" }
  }

  // ── Email accounts receivable to record payment (Larry 2026-09-15) ──
  openArModal() { if (this.hasArModalTarget) this.arModalTarget.classList.remove("hidden") }
  closeArModal() { if (this.hasArModalTarget) this.arModalTarget.classList.add("hidden") }

  async sendArRecordRequest() {
    const modal = this.arModalTarget
    const url = modal.querySelector("[data-url]")?.dataset.url
    const ids = [...modal.querySelectorAll("[data-ar-record-app]")].filter(b => b.checked).map(b => b.dataset.arRecordApp)
    if (!url) return
    if (ids.length === 0) return this._showToast("Tick at least one pay application.", "alert")
    if (this.hasArSendTarget) this.arSendTarget.disabled = true
    try {
      const resp = await fetch(url, {
        method: "POST", credentials: "same-origin", headers: this._jsonHeaders(),
        body: JSON.stringify({ pay_application_ids: ids })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Could not send the email")
      this.closeArModal()
      this._showToast(`Email sent to accounts receivable (${data.to.join(", ")}) for ${data.sent} pay application${data.sent === 1 ? "" : "s"}`)
    } catch (err) {
      this._showToast(err.message, "alert")
    } finally {
      if (this.hasArSendTarget) this.arSendTarget.disabled = false
    }
  }

  // AI suggests the state's typical retainage for the new-application modal
  // (cached server-side per state). Fills the field only while it still holds
  // the plain prefill — a value the user typed is never overwritten.
  async _suggestRetainage() {
    if (this._suggested || !this.hasSuggestUrlValue || !this.suggestUrlValue) return
    this._suggested = true

    const note = this.hasRetainageNoteTarget ? this.retainageNoteTarget : null
    if (note) note.innerHTML = `<span class="font-medium ai-rainbow-text">ConsLog AI — checking your state's typical retainage…</span>`
    try {
      const resp = await fetch(this.suggestUrlValue, { headers: { "Accept": "application/json" } })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || data.error || data.pct == null) {
        if (note) note.textContent = "Held back from each payment. Prefilled from the project setting."
        return
      }
      const untouched = parseFloat(this.retainageTarget.value) === parseFloat(this.retainageValue)
      if (untouched) this.retainageTarget.value = data.pct
      // The AI's prefill isn't something the person typed (K8-15).
      if (untouched && this._newDefaults) this._newDefaults.retainage = String(data.pct)
      if (note) {
        const applied = untouched ? "" : ` — you have ${this.retainageTarget.value}% set, edit to use it`
        note.textContent = `AI suggests ${data.pct}% for ${data.state}: ${data.note}${applied}`
      }
    } catch (e) {
      if (note) note.textContent = "Held back from each payment. Prefilled from the project setting."
    }
  }

  async create() {
    this.createBtnTarget.disabled = true
    this.createBtnTarget.textContent = "Creating…"
    try {
      const resp = await fetch(this.createUrlValue, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({
          period_start: this.periodStartTarget.value,
          period_end: this.periodEndTarget.value,
          retainage_pct: this.retainageTarget.value
        })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not create the application")
      this._forgetNewEntries()
      window.location.href = data.url
    } catch (err) {
      this._showToast(err.message, "alert")
      this.createBtnTarget.disabled = false
      this.createBtnTarget.textContent = "Create application"
    }
  }

  _headers() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }

  _showToast(message, type = "notice") {
    const div = document.createElement("div")
    div.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    div.setAttribute("data-controller", "flash")
    div.setAttribute("data-flash-dismiss-after-value", "5000")
    div.textContent = message
    document.body.prepend(div)
  }
}
